import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const GRID_SIZE = 18;
const CELL_SIZE = 25;
const MOBILE_GRID_SIZE = 14;
const MOBILE_CELL_SIZE = 20;
const INITIAL_SNAKE = [{ x: 9, y: 9 }];
const INITIAL_DIRECTION = { x: 0, y: 0 };
const GAME_SPEED = 80;

const SNAKE_COLORS = [
  { name: 'Jade', head: 'from-jade-400 to-jade-500', body: 'from-jade-500 to-jade-600', shadow: 'shadow-jade-500/50' },
  { name: 'Blue', head: 'from-blue-400 to-blue-500', body: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/50' },
  { name: 'Purple', head: 'from-purple-400 to-purple-500', body: 'from-purple-500 to-purple-600', shadow: 'shadow-purple-500/50' },
  { name: 'Red', head: 'from-red-400 to-red-500', body: 'from-red-500 to-red-600', shadow: 'shadow-red-500/50' },
  { name: 'Orange', head: 'from-orange-400 to-orange-500', body: 'from-orange-500 to-orange-600', shadow: 'shadow-orange-500/50' },
];

const POWERUP_TYPES = [
  { type: 'speed', effect: 'Speed Boost!' },
  { type: 'double', effect: 'Double Points!' },
  { type: 'invincible', effect: 'Invincible!' },
  { type: 'slow', effect: 'Slow Motion!' },
  { type: 'triple', effect: 'Triple Points!' },
  { type: 'mega', effect: 'Mega Growth!' },
];

export default function SnakeGame({ isOpen, onClose }) {
  const [isMobile, setIsMobile] = useState(false);
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [food, setFood] = useState([{ x: 13, y: 13 }, { x: 5, y: 5 }]);
  const [, setDirection] = useState(INITIAL_DIRECTION);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [highScore, setHighScore] = useState(
    parseInt(localStorage.getItem('snakeHighScore') || '0')
  );
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [wallWarning, setWallWarning] = useState(false);
  const [snakeColorIndex, setSnakeColorIndex] = useState(0);
  const [powerup, setPowerup] = useState(null);
  const [activePowerup, setActivePowerup] = useState(null);
  const [powerupEffect, setPowerupEffect] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const wallWarningTimeout = useRef(null);
  const nextDirection = useRef(INITIAL_DIRECTION);
  const powerupTimeout = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const gameContainerRef = useRef(null);

  // Sound effects
  const playSound = (soundFile) => {
    try {
      const audio = new Audio(`/${soundFile}`);
      audio.volume = 0.15;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  const generateFood = useCallback(() => {
    const currentGridSize = window.innerWidth < 640 ? MOBILE_GRID_SIZE : GRID_SIZE;
    const newFood = { x: 0, y: 0 };
    let isValid = false;
    
    while (!isValid) {
      newFood.x = Math.floor(Math.random() * currentGridSize);
      newFood.y = Math.floor(Math.random() * currentGridSize);
      isValid = !snake.some(segment => segment.x === newFood.x && segment.y === newFood.y) &&
                !food.some(f => f.x === newFood.x && f.y === newFood.y);
    }
    
    return newFood;
  }, [snake, food]);

  const generatePowerup = useCallback(() => {
    const currentGridSize = window.innerWidth < 640 ? MOBILE_GRID_SIZE : GRID_SIZE;
    // 30% chance to spawn powerup after eating food
    if (Math.random() < 0.3) {
      const newPowerup = { x: 0, y: 0 };
      let isValid = false;
      
      while (!isValid) {
        newPowerup.x = Math.floor(Math.random() * currentGridSize);
        newPowerup.y = Math.floor(Math.random() * currentGridSize);
        isValid = !snake.some(segment => segment.x === newPowerup.x && segment.y === newPowerup.y) &&
                  !food.some(f => f.x === newPowerup.x && f.y === newPowerup.y);
      }
      
      const randomType = POWERUP_TYPES[Math.floor(Math.random() * POWERUP_TYPES.length)];
      setPowerup({ ...newPowerup, ...randomType });
      
      // Remove powerup after 5 seconds
      if (powerupTimeout.current) clearTimeout(powerupTimeout.current);
      powerupTimeout.current = setTimeout(() => setPowerup(null), 5000);
    }
  }, [snake, food]);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load leaderboard from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('snakeLeaderboard');
    if (saved) {
      setLeaderboard(JSON.parse(saved));
    }
  }, []);

  const saveToLeaderboard = (name, finalScore) => {
    const newEntry = {
      name: name || 'Anonymous',
      score: finalScore,
      date: new Date().toLocaleDateString()
    };
    
    const updated = [...leaderboard, newEntry]
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // Keep top 10
    
    setLeaderboard(updated);
    localStorage.setItem('snakeLeaderboard', JSON.stringify(updated));
  };

  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setFood([{ x: 13, y: 13 }, { x: 5, y: 5 }]);
    setDirection(INITIAL_DIRECTION);
    nextDirection.current = INITIAL_DIRECTION;
    setGameOver(false);
    setScore(0);
    setIsPaused(false);
    setWallWarning(false);
    setShowLeaderboard(false);
    setPlayerName('');
    setPowerup(null);
    setActivePowerup(null);
    setPowerupEffect('');
    setGameStarted(false);
    if (wallWarningTimeout.current) {
      clearTimeout(wallWarningTimeout.current);
    }
    if (powerupTimeout.current) {
      clearTimeout(powerupTimeout.current);
    }
  };

  useEffect(() => {
    if (!isOpen || gameOver || isPaused || !gameStarted) return;

    const moveSnake = () => {
      // Sync direction from nextDirection ref at the start of each tick
      const currentDir = nextDirection.current;
      
      // Don't move if direction is 0,0 (game not started)
      if (currentDir.x === 0 && currentDir.y === 0) return;
      // Check powerup collision first (before state update)
      let powerupCollected = false;
      const head = snake[0];
      if (powerup && head.x === powerup.x && head.y === powerup.y) {
        powerupCollected = true;
        playSound('powerup.mp3');
        setActivePowerup(powerup.type);
        setPowerupEffect(powerup.effect);
        setPowerup(null);
        
        // Apply powerup effects
        if (powerup.type === 'double' || powerup.type === 'triple') {
          setTimeout(() => {
            setActivePowerup(null);
            setPowerupEffect('');
          }, 5000);
        } else if (powerup.type === 'invincible') {
          setTimeout(() => {
            setActivePowerup(null);
            setPowerupEffect('');
          }, 5000);
        } else if (powerup.type === 'speed' || powerup.type === 'slow') {
          setTimeout(() => {
            setActivePowerup(null);
            setPowerupEffect('');
          }, 3000);
        } else if (powerup.type === 'mega') {
          setTimeout(() => {
            setActivePowerup(null);
            setPowerupEffect('');
          }, 1000);
        }
      }

      setSnake(prev => {
        const currentGridSize = window.innerWidth < 640 ? MOBILE_GRID_SIZE : GRID_SIZE;
        const newHead = {
          x: prev[0].x + currentDir.x,
          y: prev[0].y + currentDir.y,
        };

        // Check wall collision with buffer
        if (newHead.x < 0 || newHead.x >= currentGridSize || newHead.y < 0 || newHead.y >= currentGridSize) {
          if (!wallWarning) {
            // First collision - show warning and give buffer time
            setWallWarning(true);
            
            // Bounce snake back one position
            newHead.x = Math.max(0, Math.min(currentGridSize - 1, newHead.x));
            newHead.y = Math.max(0, Math.min(currentGridSize - 1, newHead.y));
            
            // Set timeout to game over if they don't recover
            wallWarningTimeout.current = setTimeout(() => {
              if (wallWarning) {
                playSound('dead.mp3');
                setGameOver(true);
                if (score > highScore) {
                  setHighScore(score);
                  localStorage.setItem('snakeHighScore', score.toString());
                }
                setShowLeaderboard(true);
              }
            }, 700); // 0.7 second buffer
            
            return [newHead, ...prev.slice(0, -1)];
          } else {
            // Second collision or timeout - game over
            playSound('dead.mp3');
            setGameOver(true);
            if (score > highScore) {
              setHighScore(score);
              localStorage.setItem('snakeHighScore', score.toString());
            }
            setShowLeaderboard(true);
            return prev;
          }
        } else {
          // Clear warning if moved away from wall
          if (wallWarning) {
            setWallWarning(false);
            if (wallWarningTimeout.current) {
              clearTimeout(wallWarningTimeout.current);
            }
          }
        }

        // Check self collision (unless invincible)
        if (!activePowerup || activePowerup !== 'invincible') {
          if (prev.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
            playSound('dead.mp3');
            setGameOver(true);
            if (score > highScore) {
              setHighScore(score);
              localStorage.setItem('snakeHighScore', score.toString());
            }
            setShowLeaderboard(true);
            return prev;
          }
        }

        const newSnake = [newHead, ...prev];

        // Check food collision
        const eatenFoodIndex = food.findIndex(f => newHead.x === f.x && newHead.y === f.y);
        if (eatenFoodIndex !== -1) {
          playSound('nom.mp3');
          let points = 10;
          if (activePowerup === 'double') points = 20;
          else if (activePowerup === 'triple') points = 30;
          
          setScore(s => s + points);
          // Replace only the eaten apple with new one
          const newApple = generateFood();
          setFood(prev => {
            const newFoodArray = [...prev];
            newFoodArray[eatenFoodIndex] = newApple;
            return newFoodArray;
          });
          generatePowerup();
          
          // Mega powerup adds extra segments
          if (activePowerup === 'mega') {
            return [newHead, ...prev, ...prev.slice(-2)];
          }
        } else if (!powerupCollected) {
          // Only remove tail if no food or powerup was collected
          newSnake.pop();
        }

        return newSnake;
      });
    };

    const speed = activePowerup === 'speed' ? GAME_SPEED / 1.5 : activePowerup === 'slow' ? GAME_SPEED * 1.5 : GAME_SPEED;
    const interval = setInterval(moveSnake, speed);
    return () => clearInterval(interval);
  }, [isOpen, gameOver, isPaused, gameStarted, food, score, highScore, generateFood, wallWarning, powerup, activePowerup, generatePowerup, snake]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e) => {
      const currentDir = nextDirection.current;
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          // Prevent 180-degree turn
          if (currentDir.y !== 1) {
            playSound('up.mp3');
            nextDirection.current = { x: 0, y: -1 };
            setGameStarted(true);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          // Prevent 180-degree turn
          if (currentDir.y !== -1) {
            playSound('down.mp3');
            nextDirection.current = { x: 0, y: 1 };
            setGameStarted(true);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          // Prevent 180-degree turn
          if (currentDir.x !== 1) {
            playSound('left.mp3');
            nextDirection.current = { x: -1, y: 0 };
            setGameStarted(true);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          // Prevent 180-degree turn
          if (currentDir.x !== -1) {
            playSound('right.mp3');
            nextDirection.current = { x: 1, y: 0 };
            setGameStarted(true);
          }
          break;
        case ' ':
          e.preventDefault();
          setIsPaused(p => !p);
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);

  // Touch/Swipe controls for mobile
  useEffect(() => {
    if (!isOpen || !gameContainerRef.current) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e) => {
      if (!e.changedTouches[0]) return;
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const minSwipeDistance = 30;

      // Determine swipe direction
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Horizontal swipe
        if (Math.abs(deltaX) > minSwipeDistance) {
          const currentDir = nextDirection.current;
          if (deltaX > 0 && currentDir.x !== -1) {
            // Swipe right
            playSound('right.mp3');
            nextDirection.current = { x: 1, y: 0 };
            setGameStarted(true);
          } else if (deltaX < 0 && currentDir.x !== 1) {
            // Swipe left
            playSound('left.mp3');
            nextDirection.current = { x: -1, y: 0 };
            setGameStarted(true);
          }
        }
      } else {
        // Vertical swipe
        if (Math.abs(deltaY) > minSwipeDistance) {
          const currentDir = nextDirection.current;
          if (deltaY > 0 && currentDir.y !== -1) {
            // Swipe down
            playSound('down.mp3');
            nextDirection.current = { x: 0, y: 1 };
            setGameStarted(true);
          } else if (deltaY < 0 && currentDir.y !== 1) {
            // Swipe up
            playSound('up.mp3');
            nextDirection.current = { x: 0, y: -1 };
            setGameStarted(true);
          }
        }
      }
    };

    const container = gameContainerRef.current;
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentGridSize = isMobile ? MOBILE_GRID_SIZE : GRID_SIZE;
  const currentCellSize = isMobile ? MOBILE_CELL_SIZE : CELL_SIZE;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center backdrop-blur-sm p-2 sm:p-4 overflow-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="bg-gradient-to-br from-black via-gray-950 to-gray-900 p-3 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-2xl border-4 border-jade-500 w-full sm:w-auto mx-auto"
        >
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 flex items-center gap-2">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-jade-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="hidden sm:inline">Snake Game</span>
                <span className="sm:hidden">Snake</span>
              </h2>
              <div className="flex gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
                <span>Score: <span className="text-jade-400 font-bold">{score}</span></span>
                <span>High Score: <span className="text-jade-400 font-bold">{highScore}</span></span>
                {wallWarning && <span className="text-red-400 font-bold animate-pulse">WALL!</span>}
                {powerupEffect && <span className="text-yellow-400 font-bold animate-pulse">{powerupEffect}</span>}
              </div>
              
              {/* Color Selector */}
              <div className="flex gap-1 sm:gap-2 mt-2">
                {SNAKE_COLORS.map((color, index) => (
                  <button
                    key={color.name}
                    onClick={() => setSnakeColorIndex(index)}
                    className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br ${color.head} border-2 ${
                      snakeColorIndex === index ? 'border-white scale-110' : 'border-gray-600'
                    } transition-all duration-200 hover:scale-110`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowLeaderboard(!showLeaderboard)}
                className="text-gray-400 hover:text-jade-400 transition-colors"
                title="Leaderboard"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div
            ref={gameContainerRef}
            className="relative bg-gradient-to-br from-black via-gray-950 to-black rounded-lg border-4 border-jade-500 shadow-2xl shadow-jade-500/50 overflow-hidden mx-auto"
            style={{ width: currentGridSize * currentCellSize, height: currentGridSize * currentCellSize }}
          >
            {/* Grid Background */}
            <svg className="absolute inset-0 w-full h-full opacity-60" style={{ pointerEvents: 'none' }}>
              <defs>
                <pattern id="grid" width={currentCellSize} height={currentCellSize} patternUnits="userSpaceOnUse">
                  <rect width={currentCellSize} height={currentCellSize} fill="none" stroke="#10b981" strokeWidth="2" opacity="0.8"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            {/* Snake */}
            {snake.map((segment, index) => {
              const selectedColor = SNAKE_COLORS[snakeColorIndex];
              const currentSpeed = activePowerup === 'speed' ? GAME_SPEED / 1.5 : activePowerup === 'slow' ? GAME_SPEED * 1.5 : GAME_SPEED;
              return (
                <div
                  key={index}
                  className={`absolute ${
                    index === 0 
                      ? `bg-gradient-to-br ${selectedColor.head} shadow-2xl ${selectedColor.shadow} border-2 border-white` 
                      : `bg-gradient-to-br ${selectedColor.body} border border-white/60`
                  }`}
                  style={{
                    width: currentCellSize - 2,
                    height: currentCellSize - 2,
                    left: segment.x * currentCellSize + 1,
                    top: segment.y * currentCellSize + 1,
                    borderRadius: index === 0 ? '6px' : '3px',
                    transition: `all ${currentSpeed}ms linear`,
                    boxShadow: index === 0 ? '0 0 15px rgba(255, 255, 255, 0.5)' : undefined,
                  }}
                />
              );
            })}

            {/* Food */}
            {food.map((apple, index) => (
              <div
                key={index}
                className="absolute bg-gradient-to-br from-red-300 to-red-500 shadow-2xl shadow-red-600/80 border-2 border-red-200"
                style={{
                  width: currentCellSize - 2,
                  height: currentCellSize - 2,
                  left: apple.x * currentCellSize + 1,
                  top: apple.y * currentCellSize + 1,
                  borderRadius: '50%',
                  animation: 'breathe 2s ease-in-out infinite',
                  boxShadow: '0 0 20px rgba(239, 68, 68, 1), 0 0 40px rgba(239, 68, 68, 0.8)',
                }}
              />
            ))}

            {/* Powerup */}
            {powerup && (
              <div
                className="absolute flex items-center justify-center shadow-2xl animate-bounce border-2 border-yellow-300 rounded-full bg-yellow-100/20"
                style={{
                  width: currentCellSize + 4,
                  height: currentCellSize + 4,
                  left: powerup.x * currentCellSize - 2,
                  top: powerup.y * currentCellSize - 2,
                  boxShadow: '0 0 30px rgba(250, 204, 21, 1), 0 0 60px rgba(250, 204, 21, 0.8), inset 0 0 20px rgba(250, 204, 21, 0.5)',
                }}
              >
                <svg 
                  className="w-full h-full p-1 text-yellow-300 drop-shadow-2xl"
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    filter: 'drop-shadow(0 0 12px rgba(250, 204, 21, 1)) brightness(1.5)',
                  }}
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
            )}

            {/* Game Start Overlay */}
            {!gameStarted && !gameOver && (
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <div className="mb-6">
                    <svg className="w-20 h-20 text-jade-400 mx-auto animate-bounce-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">Ready to Play?</h3>
                  <div className="space-y-2 text-jade-400">
                    {isMobile ? (
                      <>
                        <p className="text-sm sm:text-base md:text-lg font-semibold animate-pulse">Swipe to Start & Move</p>
                        <div className="flex justify-center gap-3 mt-4 text-white">
                          <div className="flex flex-col items-center">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                            </svg>
                            <span className="text-xs mt-1">Swipe Up</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                            </svg>
                            <span className="text-xs mt-1">Swipe Down</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm sm:text-base md:text-lg font-semibold animate-pulse">Press Any Arrow Key to Start</p>
                        <div className="flex justify-center gap-2 mt-4">
                          <kbd className="px-2 py-1 sm:px-3 sm:py-2 bg-gray-800 border border-jade-500/50 rounded text-white text-xs">↑</kbd>
                          <kbd className="px-2 py-1 sm:px-3 sm:py-2 bg-gray-800 border border-jade-500/50 rounded text-white text-xs">↓</kbd>
                          <kbd className="px-2 py-1 sm:px-3 sm:py-2 bg-gray-800 border border-jade-500/50 rounded text-white text-xs">←</kbd>
                          <kbd className="px-2 py-1 sm:px-3 sm:py-2 bg-gray-800 border border-jade-500/50 rounded text-white text-xs">→</kbd>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Game Over Overlay */}
            {gameOver && !showLeaderboard && (
              <div className="absolute inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center rounded-lg p-2">
                <div className="text-center">
                  <div className="mb-3 sm:mb-4">
                    <svg className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">Game Over!</h3>
                  <p className="text-jade-400 text-base sm:text-lg mb-3 sm:mb-4">Final Score: <span className="font-bold">{score}</span></p>
                  
                  {/* Name input for leaderboard */}
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-40 sm:w-48 px-3 py-2 sm:px-4 mb-3 sm:mb-4 bg-gray-800 text-white text-sm sm:text-base rounded-lg border border-jade-500/30 focus:border-jade-500 focus:outline-none"
                    maxLength={15}
                  />
                  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                    <button
                      onClick={() => {
                        saveToLeaderboard(playerName, score);
                        setShowLeaderboard(true);
                      }}
                      className="px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-jade-500 to-jade-600 hover:from-jade-600 hover:to-jade-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-jade-500/50 transform hover:scale-105"
                    >
                      Save Score
                    </button>
                    <button
                      onClick={resetGame}
                      className="px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300"
                    >
                      Play Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Leaderboard Overlay */}
            {showLeaderboard && (
              <div className="absolute inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center rounded-lg p-2 sm:p-4 overflow-auto">
                <div className="text-center w-full max-w-md">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-jade-400 mb-3 sm:mb-4 flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Leaderboard
                  </h3>
                  
                  <div className="bg-gray-900/50 rounded-lg p-2 sm:p-4 mb-3 sm:mb-4 max-h-48 sm:max-h-64 overflow-y-auto">
                    {leaderboard.length === 0 ? (
                      <p className="text-gray-400 text-sm">No scores yet. Be the first!</p>
                    ) : (
                      <table className="w-full text-left text-xs sm:text-sm">
                        <thead className="text-gray-400 border-b border-gray-700">
                          <tr>
                            <th className="py-1 px-1 sm:py-2 sm:px-2">#</th>
                            <th className="py-1 px-1 sm:py-2 sm:px-2">Name</th>
                            <th className="py-1 px-1 sm:py-2 sm:px-2 text-right">Score</th>
                            <th className="py-1 px-1 sm:py-2 sm:px-2 text-right hidden sm:table-cell">Date</th>
                          </tr>
                        </thead>
                        <tbody className="text-white">
                          {leaderboard.map((entry, index) => (
                            <tr key={index} className="border-b border-gray-800">
                              <td className="py-1 px-1 sm:py-2 sm:px-2">
                                {index === 0 && '🥇'}
                                {index === 1 && '🥈'}
                                {index === 2 && '🥉'}
                                {index > 2 && index + 1}
                              </td>
                              <td className="py-1 px-1 sm:py-2 sm:px-2 font-medium">{entry.name}</td>
                              <td className="py-1 px-1 sm:py-2 sm:px-2 text-right text-jade-400 font-bold">{entry.score}</td>
                              <td className="py-1 px-1 sm:py-2 sm:px-2 text-right text-gray-400 text-xs hidden sm:table-cell">{entry.date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  
                  <button
                    onClick={resetGame}
                    className="px-4 py-2 sm:px-8 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-jade-500 to-jade-600 hover:from-jade-600 hover:to-jade-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-jade-500/50 transform hover:scale-105"
                  >
                    Play Again
                  </button>
                </div>
              </div>
            )}

            {/* Pause Overlay */}
            {isPaused && !gameOver && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <svg className="w-12 h-12 sm:w-16 sm:h-16 text-jade-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">PAUSED</h3>
                  <p className="text-gray-400 text-xs sm:text-sm mt-2">Press Space to continue</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 sm:mt-4 text-center text-xs sm:text-sm text-gray-400">
            {isMobile ? (
              <p>Swipe to move • Tap outside to close</p>
            ) : (
              <p>Use Arrow Keys to move • Space to pause • ESC to close</p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
